import {
  ChangePasswordRequest,
  ChangePasswordResponse,
  DomainRedirectError,
  LoginCredentials,
  LoginResponse,
  MagicLinkRequestResponse,
  MagicLinkVerifyResponse,
  MFAChallengeResponse,
  MFAEnrollResponse,
  MFAStatusResponse,
  MFAVerifyResponse,
  Passkey,
  PasskeyLoginOptionsResponse,
  PasskeyLoginVerifyResponse,
  PasskeyRegistrationOptionsResponse,
  PasskeyRegistrationVerifyResponse,
  UpdateUserRequest,
  UserInfo,
} from "@/types/auth";

import { useAuthStore } from "@/stores/auth-store";

// The access token now lives in the in-memory auth store (#39). Fall back to a transient
// localStorage token only for the mobile WebView (which injects it). The MFA login flow
// reads its pre-MFA token directly from the dedicated "mfa-pending-token" key instead.
function getAccessToken(): string | null {
  const fromStore = useAuthStore.getState().user?.token;
  if (fromStore) return fromStore;
  if (typeof window !== "undefined") return localStorage.getItem("auth-token");
  return null;
}

// CSRF header for the cookie-borne refresh/logout endpoints (#39).
const CSRF_HEADERS: Record<string, string> = {
  "X-Requested-With": "XMLHttpRequest",
};

// Helper to convert ArrayBuffer to base64url (not standard base64)
function arrayBufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  // Convert to base64, then to base64url
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export const authApi = {
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    // Use new v2 auth endpoint
    const response = await fetch("/api/v1/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    });

    const data = await response.json();

    // Check for domain redirect (user should log in from their branded domain)
    if (response.status === 307 && data.redirect) {
      throw new DomainRedirectError(
        data.redirect,
        data.message || "Please use your branded domain to log in"
      );
    }

    if (!response.ok) {
      throw new Error(data.error || "Login failed");
    }

    // Transform response to match expected structure
    return {
      token: data.token,
      refreshToken: data.refreshToken,
      userInfo: data.user, // New response includes user data directly
      mfaRequired: data.mfaRequired,
      mfaFactors: data.mfaFactors,
    };
  },

  getUserInfo: async (): Promise<UserInfo> => {
    // Use new v2 auth endpoint
    if (typeof window === "undefined") {
      throw new Error("Cannot get user info during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/user", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch user info");
    }

    const data = await response.json();
    return data;
  },

  logout: async (): Promise<void> => {
    // Skip during SSR
    if (typeof window === "undefined") {
      return;
    }

    // credentials:'include' sends the HttpOnly refresh cookie so the server can clear
    // it; the CSRF header is required on the cookie path (#39).
    const token = getAccessToken();
    try {
      await fetch("/api/v1/auth/logout", {
        method: "POST",
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...CSRF_HEADERS,
        },
        credentials: "include",
      });
    } catch {
      // ignore — local cleanup happens regardless
    }
    // Clear any legacy/transient JS-readable tokens regardless of API response.
    localStorage.removeItem("auth-token");
    localStorage.removeItem("refresh-token");
  },

  // NOTE: the auth store owns the coordinated refresh path (single in-flight promise +
  // cross-tab lock); prefer useAuthStore().refreshToken(). This direct helper exists for
  // completeness and uses the same cookie model: credentials:'include' sends the HttpOnly
  // cookie, the CSRF header is required, and a legacy token (if passed) is the dual-read
  // body fallback.
  refreshToken: async (
    refreshToken?: string
  ): Promise<{ token: string; refreshToken: string }> => {
    const response = await fetch("/api/v1/auth/refresh", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...CSRF_HEADERS,
      },
      credentials: "include",
      body: JSON.stringify(refreshToken ? { refreshToken } : {}),
    });

    if (!response.ok) {
      throw new Error("Token refresh failed");
    }

    return response.json();
  },

  updateUser: async (data: { fullName: string }): Promise<UserInfo> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot update user during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/user", {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to update user");
    }

    return response.json();
  },

  changePassword: async (
    data: ChangePasswordRequest
  ): Promise<ChangePasswordResponse> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot change password during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/change-password", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to change password");
    }

    return response.json();
  },

  forgotPassword: async (email: string): Promise<{ message: string }> => {
    const response = await fetch("/api/v1/auth/forgot-password", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to request password reset");
    }

    return response.json();
  },

  validateResetToken: async (
    token: string
  ): Promise<{ valid: boolean; error?: string }> => {
    const response = await fetch(
      `/api/v1/auth/reset-password/${encodeURIComponent(token)}`,
      {
        method: "GET",
      }
    );

    return response.json();
  },

  resetPassword: async (
    token: string,
    newPassword: string
  ): Promise<{ message: string }> => {
    const response = await fetch("/api/v1/auth/reset-password", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token, newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to reset password");
    }

    return response.json();
  },

  verifyEmail: async (token: string): Promise<{ message: string }> => {
    const response = await fetch("/api/v1/auth/verify-email", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to verify email");
    }

    return response.json();
  },

  resendVerification: async (email: string): Promise<{ message: string }> => {
    const response = await fetch("/api/v1/auth/resend-verification", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to resend verification email");
    }

    return response.json();
  },

  // MFA Methods
  getMfaStatus: async (): Promise<MFAStatusResponse> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot get MFA status during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/mfa/status", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to get MFA status");
    }

    return response.json();
  },

  enrollMfa: async (friendlyName?: string): Promise<MFAEnrollResponse> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot enroll MFA during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/mfa/enroll", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        friendlyName: friendlyName || "Authenticator App",
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to enroll MFA");
    }

    return response.json();
  },

  confirmMfa: async (
    factorId: string,
    code: string
  ): Promise<{ message: string }> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot confirm MFA during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/mfa/confirm", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ factorId, code }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to confirm MFA");
    }

    return response.json();
  },

  createMfaChallenge: async (
    factorId: string
  ): Promise<MFAChallengeResponse> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot create MFA challenge during SSR");
    }
    // Login-flow MFA: authenticate with the pre-MFA token, not a real session.
    const token = localStorage.getItem("mfa-pending-token");
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/mfa/challenge", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ factorId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create MFA challenge");
    }

    return response.json();
  },

  verifyMfa: async (
    factorId: string,
    challengeId: string,
    code: string
  ): Promise<MFAVerifyResponse> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot verify MFA during SSR");
    }
    // Login-flow MFA: authenticate with the pre-MFA token, not a real session.
    const token = localStorage.getItem("mfa-pending-token");
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/mfa/verify", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ factorId, challengeId, code }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to verify MFA");
    }

    return response.json();
  },

  disableMfa: async (
    factorId: string,
    code: string,
    useEmailCode?: boolean
  ): Promise<{ message: string }> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot disable MFA during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/mfa", {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ factorId, code, useEmailCode }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to disable MFA");
    }

    return response.json();
  },

  // Request email verification link to disable MFA (for when authenticator is lost)
  requestMfaDisableLink: async (): Promise<{ message: string }> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot request MFA disable link during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/mfa/disable-link", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to send verification email");
    }

    return response.json();
  },

  // Magic Link Methods
  requestMagicLink: async (
    email: string
  ): Promise<MagicLinkRequestResponse> => {
    const response = await fetch("/api/v1/auth/magic-link/request", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to request magic link");
    }

    return response.json();
  },

  verifyMagicLink: async (options: {
    code?: string;
    token?: string;
    email?: string;
  }): Promise<MagicLinkVerifyResponse> => {
    const response = await fetch("/api/v1/auth/magic-link/verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to verify magic link");
    }

    return response.json();
  },

  // Passkey (WebAuthn) Methods
  getPasskeyRegistrationOptions: async (
    name?: string
  ): Promise<PasskeyRegistrationOptionsResponse> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot get passkey options during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/passkey/register-options", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.error || "Failed to get passkey registration options"
      );
    }

    return response.json();
  },

  verifyPasskeyRegistration: async (
    challengeId: string,
    credential: PublicKeyCredential,
    name?: string
  ): Promise<PasskeyRegistrationVerifyResponse> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot verify passkey during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    // Convert credential to JSON-serializable format with base64url encoding
    const attestationResponse =
      credential.response as AuthenticatorAttestationResponse;
    const credentialJSON = {
      id: credential.id,
      rawId: arrayBufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        attestationObject: arrayBufferToBase64url(
          attestationResponse.attestationObject
        ),
        clientDataJSON: arrayBufferToBase64url(
          credential.response.clientDataJSON
        ),
      },
    };

    const response = await fetch("/api/v1/auth/passkey/register-verify", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ challengeId, credential: credentialJSON, name }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to verify passkey registration");
    }

    return response.json();
  },

  getPasskeyLoginOptions: async (
    email?: string
  ): Promise<PasskeyLoginOptionsResponse> => {
    const response = await fetch("/api/v1/auth/passkey/login-options", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to get passkey login options");
    }

    return response.json();
  },

  verifyPasskeyLogin: async (
    challengeId: string,
    credential: PublicKeyCredential
  ): Promise<LoginResponse> => {
    // Convert credential to JSON-serializable format with base64url encoding
    const assertionResponse =
      credential.response as AuthenticatorAssertionResponse;
    const credentialJSON = {
      id: credential.id,
      rawId: arrayBufferToBase64url(credential.rawId),
      type: credential.type,
      response: {
        authenticatorData: arrayBufferToBase64url(
          assertionResponse.authenticatorData
        ),
        clientDataJSON: arrayBufferToBase64url(
          credential.response.clientDataJSON
        ),
        signature: arrayBufferToBase64url(assertionResponse.signature),
        userHandle: assertionResponse.userHandle
          ? arrayBufferToBase64url(assertionResponse.userHandle)
          : null,
      },
    };

    const response = await fetch("/api/v1/auth/passkey/login-verify", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ challengeId, credential: credentialJSON }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to verify passkey login");
    }

    const data = await response.json();

    // Transform response to match LoginResponse format expected by auth store
    return {
      token: data.token,
      refreshToken: data.refreshToken,
      userInfo: data.user, // Map 'user' to 'userInfo' for consistency
    };
  },

  listPasskeys: async (): Promise<Passkey[]> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot list passkeys during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch("/api/v1/auth/passkeys", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to list passkeys");
    }

    return response.json();
  },

  deletePasskey: async (passkeyId: string): Promise<{ message: string }> => {
    if (typeof window === "undefined") {
      throw new Error("Cannot delete passkey during SSR");
    }
    const token = getAccessToken();
    if (!token) {
      throw new Error("No authentication token");
    }

    const response = await fetch(`/api/v1/auth/passkey/${passkeyId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to delete passkey");
    }

    return response.json();
  },

  // User Invitation Methods
  validateInvitation: async (
    token: string
  ): Promise<{
    valid: boolean;
    email?: string;
    entityType?: string;
    entityName?: string;
    inviterName?: string;
    error?: string;
  }> => {
    const response = await fetch(
      `/api/v1/auth/invitation/${encodeURIComponent(token)}`,
      {
        method: "GET",
      }
    );

    return response.json();
  },

  acceptInvitation: async (data: {
    token: string;
    fullName: string;
    password: string;
  }): Promise<{ message: string }> => {
    const response = await fetch("/api/v1/auth/invitation/accept", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to accept invitation");
    }

    return response.json();
  },
};
